import type { KeyboardEvent, ReactElement } from 'react'
import { useId, useMemo, useRef, useState } from 'react'

const COMBOBOX_CSS = `
.nw-combo { display: flex; flex-direction: column; gap: 4px; position: relative; }
/* Outranked by the Sources fieldset rule everywhere this component is used today,
   so it does not render. It reads the token anyway: the first use outside a
   fieldset is exactly when nobody would be checking its tracking. */
.nw-combo > label { color: var(--nw-muted); font-size: var(--nw-text-chip); letter-spacing: var(--nw-track-label); text-transform: uppercase; }
.nw-combo-row { position: relative; }
.nw-combo-row input[type='text'] {
  background: var(--nw-plane); border: 1px solid var(--nw-hairline); border-radius: 8px;
  box-sizing: border-box; color: inherit; font: inherit; padding: 7px 34px 7px 9px; width: 100%;
}
.nw-combo-row input[disabled] { cursor: not-allowed; opacity: .6; }
/* Fitted to the slot the input leaves it rather than taken off the type scale:
   it is a glyph centred in 34px of padding, and a step would size the caret by
   a rule about prose. Deliberately outside the scale. */
.nw-combo-open {
  background: none; border: 0; color: var(--nw-muted); cursor: pointer; font-size: 10px;
  line-height: 1; padding: 8px; position: absolute; right: 2px; top: 50%; transform: translateY(-50%);
}
.nw-combo-pop {
  background: var(--nw-surface); border: 1px solid var(--nw-hairline); border-radius: 8px;
  box-shadow: var(--nw-shadow-menu); box-sizing: border-box; left: 0; margin-top: 4px;
  max-height: 260px; overflow-y: auto; position: absolute; right: 0; top: 100%; z-index: 20;
}
.nw-combo-pop ul { list-style: none; margin: 0; padding: 4px; }
.nw-combo-pop li {
  border-radius: 6px; cursor: pointer; font-size: var(--nw-text-body); letter-spacing: 0; padding: 7px 9px;
  text-transform: none;
}
.nw-combo-pop li[aria-selected='true'] { background: var(--nw-ink); color: var(--nw-surface); }
.nw-combo-pop li[data-chosen='true'] { font-weight: 600; }
.nw-combo-empty { color: var(--nw-ink-2); font-size: var(--nw-text-cell); letter-spacing: 0; margin: 0; padding: 10px; text-transform: none; }
.nw-combo-why { color: var(--nw-serious); font-size: var(--nw-text-chip); letter-spacing: 0; text-transform: none; }

/* A list the width of a thumb, over a software keyboard that has taken half the
   screen: capped short enough that the box it filters stays visible, with rows
   big enough to hit without aiming. */
@media (max-width: 600px) {
  .nw-combo-pop { max-height: 40vh; }
  .nw-combo-pop li { font-size: var(--nw-text-field); padding: 11px 10px; }
}
`

export interface ComboboxOption {
  value: string
  label: string
}

export interface ComboboxProps {
  label: string
  options: readonly ComboboxOption[]
  /** The chosen option's value, or '' while nothing is chosen. */
  value: string
  onChange: (value: string) => void
  id?: string
  /** Submitted with the enclosing form under this name, when given. */
  name?: string
  /** Plural, in the owner's words: "banks", "currencies". Spoken, and shown when nothing matches. */
  noun?: string
  placeholder?: string
  /** Why there is nothing to choose from. Given, the field says so and takes no input. */
  unavailable?: string
}

const NONE = -1

/** Folded flat, so Ålandsbanken is reachable from a keyboard that has no Å. */
function fold(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
}

export function matching(
  options: readonly ComboboxOption[],
  query: string,
): readonly ComboboxOption[] {
  const needle = fold(query.trim())
  if (needle === '') return options
  return options.filter((option) => fold(option.label).includes(needle))
}

/**
 * One box that filters a list, for the fields whose answer is one of very many
 * — a bank out of every bank in the market, and in time an entity, an
 * instrument kind, a currency.
 *
 * It is the APG's editable combobox with a listbox popup and list autocomplete
 * with *manual* selection: typing narrows the list and does nothing else.
 * Automatic selection would leave the first match standing as the answer, and
 * the answer here decides which bank somebody is sent to for a MitID approval —
 * a value that arrived because the filter sorted that way is a wasted trip, and
 * nothing on the page can tell it apart from a choice.
 *
 * Focus never leaves the box; the highlight moves by `aria-activedescendant`,
 * so what has been typed and which option is being considered are read from the
 * same place. A combobox that only answers a mouse would be worse than the
 * `select` it replaces, which was already whole.
 */
export function Combobox({
  label,
  options,
  value,
  onChange,
  id,
  name,
  noun = 'options',
  placeholder,
  unavailable,
}: ComboboxProps): ReactElement {
  const generated = useId()
  const fieldId = id ?? generated
  const listId = `${fieldId}-list`
  const optionId = (index: number): string => `${fieldId}-option-${index}`

  const chosen = options.find((option) => option.value === value)
  const [query, setQuery] = useState<string | null>(null)
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(NONE)
  const [said, setSaid] = useState('')
  const listRef = useRef<HTMLUListElement>(null)

  // The box shows what is being typed while something is, and otherwise the
  // chosen option's own words. Kept as the absence of a query rather than
  // copied into state, so a value that changes from outside — a list arriving,
  // a form clearing itself — needs no effect to stay in step.
  const text = query ?? chosen?.label ?? ''
  const matches = useMemo(
    () => (query === null ? options : matching(options, query)),
    [options, query],
  )
  const highlighted = active >= 0 && active < matches.length ? active : NONE

  const counted = (found: number): string => {
    if (found === 0) return `No ${noun} match.`
    return found === 1 ? '1 match.' : `${found} matches.`
  }

  const reveal = (index: number): void => {
    setActive(index)
    const option = listRef.current?.children[index]
    if (option instanceof HTMLElement) option.scrollIntoView?.({ block: 'nearest' })
  }

  const choose = (option: ComboboxOption): void => {
    onChange(option.value)
    setQuery(null)
    setOpen(false)
    setActive(NONE)
    setSaid(`${option.label} chosen.`)
  }

  /** Leaves the value alone and puts the box back to what that value is called. */
  const dismiss = (): void => {
    setQuery(null)
    setOpen(false)
    setActive(NONE)
  }

  const show = (index: number): void => {
    setOpen(true)
    setSaid(counted(matches.length))
    if (index !== NONE && matches.length > 0) reveal(index)
  }

  const step = (delta: number): void => {
    if (matches.length === 0) return
    const from = highlighted === NONE ? (delta > 0 ? -1 : 0) : highlighted
    reveal((from + delta + matches.length) % matches.length)
  }

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        if (open) step(1)
        else show(event.altKey ? NONE : 0)
        return
      case 'ArrowUp':
        event.preventDefault()
        if (open) step(-1)
        else show(matches.length - 1)
        return
      case 'Enter': {
        if (!open) return
        // The open list covers the button this form would otherwise submit, so
        // a press aimed at the list is never also a submission.
        event.preventDefault()
        const picked = matches[highlighted]
        if (picked !== undefined) choose(picked)
        else setOpen(false)
        return
      }
      case 'Escape':
        event.preventDefault()
        dismiss()
        return
      case 'Tab':
        dismiss()
        return
      default:
    }
  }

  const typed = (next: string): void => {
    setQuery(next)
    setOpen(true)
    setActive(NONE)
    setSaid(counted(matching(options, next).length))
  }

  const announcement = (
    <span className="nw-sr-only" role="status">
      {said}
    </span>
  )

  // The caption sits beside the field rather than around it: a label that wraps
  // the open button and the list lends all of their words to the field's own
  // name, and "Bank Show banks" is what a screen reader would then read out.
  if (unavailable !== undefined) {
    return (
      <div className="nw-combo">
        <style>{COMBOBOX_CSS}</style>
        <label htmlFor={fieldId}>{label}</label>
        <div className="nw-combo-row">
          <input
            id={fieldId}
            type="text"
            value=""
            disabled
            readOnly
            aria-describedby={`${fieldId}-aside`}
          />
        </div>
        <small id={`${fieldId}-aside`} className="nw-combo-why">
          {unavailable}
        </small>
        {announcement}
        {name !== undefined && <input type="hidden" name={name} value={value} />}
      </div>
    )
  }

  return (
    <div className="nw-combo">
      <style>{COMBOBOX_CSS}</style>
      <label htmlFor={fieldId}>{label}</label>
      <div className="nw-combo-row">
        <input
          id={fieldId}
          type="text"
          role="combobox"
          value={text}
          placeholder={placeholder}
          autoComplete="off"
          autoCapitalize="none"
          spellCheck={false}
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={listId}
          {...(highlighted === NONE ? {} : { 'aria-activedescendant': optionId(highlighted) })}
          onChange={(event) => typed(event.target.value)}
          onKeyDown={onKeyDown}
          onBlur={dismiss}
        />
        <button
          type="button"
          className="nw-combo-open"
          tabIndex={-1}
          aria-label={`Show ${noun}`}
          aria-expanded={open}
          aria-controls={listId}
          // A press on the button, or on an option, must not take focus off the
          // box: the box is where the keyboard is, and where a screen reader is
          // reading the highlight from.
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => (open ? dismiss() : show(NONE))}
        >
          ▼
        </button>

        {open && (
          <div className="nw-combo-pop" onMouseDown={(event) => event.preventDefault()}>
            <ul id={listId} role="listbox" aria-label={label} ref={listRef}>
              {matches.map((option, index) => (
                <li
                  key={option.value}
                  id={optionId(index)}
                  role="option"
                  aria-selected={index === highlighted}
                  data-chosen={option.value === value}
                  onClick={() => choose(option)}
                >
                  {option.label}
                </li>
              ))}
            </ul>
            {matches.length === 0 && (
              <p className="nw-combo-empty">
                No {noun} match “{text}”.
              </p>
            )}
          </div>
        )}
      </div>
      {announcement}
      {name !== undefined && <input type="hidden" name={name} value={value} />}
    </div>
  )
}
